// Household income arithmetic and the month grid an income history type implies.
//
// The formula mirrors `BenefitIncomeMath.MonthlyAdjusted` and, through
// it, `BenefitsQueryExtensions.GetAdjustedMonthlyIncome`: for each reported source,
// `(sum of the monthly amounts / periods) * (1 - adjustment rate)`, summed across the
// household. Periods follow the history type — 1, 3, 12, and 1 for year-to-date, which the
// legacy computation treats as already monthly.
//
// The client computes these figures only to show staff the effect of what they are typing.
// The saved value is whatever the server returns on the next read.

/** `IncomeHistoryType` bytes. */
export const ONE_MONTH = 0
export const THREE_MONTH = 1
export const TWELVE_MONTH = 2
export const YEAR_TO_DATE = 3

export const incomeHistoryLabels: Record<number, string> = {
  [ONE_MONTH]: "One Month",
  [THREE_MONTH]: "Three Month",
  [TWELVE_MONTH]: "Twelve Month",
  [YEAR_TO_DATE]: "Year To Date",
}

/** `HouseholdMemberIncomeAdjustment` values. */
export const ADJUSTMENT_NOT_ANSWERED = 0
export const ADJUSTMENT_NONE = 1
export const ADJUSTMENT_TWENTY_PERCENT = 2
export const ADJUSTMENT_FIFTEEN_PERCENT = 3
export const ADJUSTMENT_TEN_PERCENT = 4

/**
 * The choices offered for a new or edited income row. 15 % is deliberately absent: both
 * adjusted-income computations read only the 20 % and 10 % flags, so choosing it would
 * change nothing while looking like it did.
 */
export const adjustmentOptions = [
  { value: String(ADJUSTMENT_NONE), label: "None" },
  { value: String(ADJUSTMENT_TWENTY_PERCENT), label: "20%" },
  { value: String(ADJUSTMENT_TEN_PERCENT), label: "10%" },
]

export function adjustmentLabel(adjustment: number): string {
  switch (adjustment) {
    case ADJUSTMENT_NONE:
      return "None"
    case ADJUSTMENT_TWENTY_PERCENT:
      return "20%"
    case ADJUSTMENT_FIFTEEN_PERCENT:
      return "15% (legacy)"
    case ADJUSTMENT_TEN_PERCENT:
      return "10%"
    default:
      return "Not answered"
  }
}

/**
 * The effective rate of an adjustment. 15 % is zero because the server's arithmetic never
 * reads its flag; showing anything else here would put a number on screen that no saved
 * total agrees with.
 */
export function adjustmentRate(adjustment: number): number {
  switch (adjustment) {
    case ADJUSTMENT_TWENTY_PERCENT:
      return 0.2
    case ADJUSTMENT_TEN_PERCENT:
      return 0.1
    default:
      return 0
  }
}

/** Reads the four mutually exclusive legacy booleans back as one choice. */
export function adjustmentFromFlags(flags: {
  a1: boolean
  a2: boolean
  a3: boolean
  aNone: boolean
}): number {
  if (flags.a1) return ADJUSTMENT_TWENTY_PERCENT
  if (flags.a2) return ADJUSTMENT_FIFTEEN_PERCENT
  if (flags.a3) return ADJUSTMENT_TEN_PERCENT
  if (flags.aNone) return ADJUSTMENT_NONE
  return ADJUSTMENT_NOT_ANSWERED
}

/** The number of monthly amounts a history type carries; the server enforces this exactly. */
export function requiredMonthCount(incomeType: number): number {
  switch (incomeType) {
    case THREE_MONTH:
      return 3
    case TWELVE_MONTH:
      return 12
    default:
      return 1
  }
}

/** The divisor that turns a gross total into a monthly figure. */
export function monthlyDivisor(incomeType: number): number {
  return requiredMonthCount(incomeType)
}

function firstOfMonth(date: Date): string {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, "0")
  return `${year}-${month}-01`
}

/**
 * The months an income row covers, newest first, each the first of its month. The series
 * starts with the month before `asOf` because the current month is not complete yet.
 */
export function monthsFor(incomeType: number, asOf: Date): string[] {
  const months: string[] = []
  for (let index = 0; index < requiredMonthCount(incomeType); index++) {
    months.push(firstOfMonth(new Date(asOf.getFullYear(), asOf.getMonth() - 1 - index, 1)))
  }
  return months
}

/** Date-only application dates are local business dates, not UTC instants. */
export function applicationIncomeAnchor(applicationDate: string | null | undefined, fallback = new Date()): Date {
  if (!applicationDate || !/^\d{4}-\d{2}-\d{2}$/.test(applicationDate.slice(0, 10))) return fallback
  const [year, month, day] = applicationDate.slice(0, 10).split("-").map(Number)
  const date = new Date(year!, month! - 1, day!)
  return Number.isFinite(date.getTime()) ? date : fallback
}

/** The next month's first day generates a history ending in the explicitly selected month. */
export function incomeMonthAnchor(month: string): Date | null {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return null
  const [year, number] = month.split("-").map(Number)
  return new Date(year!, number!, 1)
}

/** Changing history length must stay anchored to the reported months, never today's clock. */
export function incomeHistoryAnchor(amounts: readonly { date: string }[], fallback: Date): Date {
  const month = amounts.map(a => a.date.slice(0, 7)).sort().at(-1)
  return (month ? incomeMonthAnchor(month) : null) ?? fallback
}

export interface IncomeAmountEntry {
  id?: string
  date: string
  amount: string
}

/**
 * Regenerates the month grid for a new history type, keeping the amount already entered for
 * every month that survives the change. Shortening a history drops the months it no longer
 * covers; lengthening it adds blank ones rather than inventing figures.
 */
export function regenerateAmounts(
  current: IncomeAmountEntry[],
  incomeType: number,
  asOf: Date,
): IncomeAmountEntry[] {
  const existing = new Map(current.map((entry) => [entry.date.slice(0, 7), entry]))
  return monthsFor(incomeType, asOf).map((date) => {
    const kept = existing.get(date.slice(0, 7))
    // An observed legacy day is evidence too: retain it, its amount and its row ID.
    return kept ? { ...kept } : { date, amount: "" }
  })
}

export function parseAmount(value: string): number {
  const parsed = Number(value.trim().replace(/[$,]/g, ""))
  return Number.isFinite(parsed) ? parsed : 0
}

export interface IncomeLineFigures {
  gross: number
  monthly: number
  monthlyAdjusted: number
}

export function incomeLineFigures(
  amounts: readonly { amount: string }[],
  incomeType: number,
  adjustment: number,
): IncomeLineFigures {
  const gross = amounts.reduce((total, entry) => total + parseAmount(entry.amount), 0)
  const monthly = gross / monthlyDivisor(incomeType)
  return {
    gross,
    monthly,
    monthlyAdjusted: monthly * (1 - adjustmentRate(adjustment)),
  }
}

/** The household figure, summed the same way the server sums it. */
export function householdMonthlyAdjustedIncome(
  lines: readonly {
    amounts: readonly { amount: string }[]
    incomeType: number
    adjustment: number
  }[],
): number {
  return lines.reduce(
    (total, line) =>
      total + incomeLineFigures(line.amounts, line.incomeType, line.adjustment).monthlyAdjusted,
    0,
  )
}

/**
 * Blocks a save the server would reject anyway: the month count has to match the history
 * type exactly, and no month may appear twice.
 */
export function validateIncomeLine(
  amounts: readonly { date: string; amount?: string | number }[],
  incomeType: number,
  sourceOfIncome?: number,
): string | null {
  const required = requiredMonthCount(incomeType)
  if (amounts.length !== required) {
    return `${incomeHistoryLabels[incomeType] ?? "This"} income requires exactly ${required} monthly amount(s).`
  }

  const months = amounts.map((entry) => entry.date.slice(0, 7))
  if (new Set(months).size !== months.length) {
    return "Each monthly amount must cover a different month."
  }

  for (const entry of amounts) {
    if (entry.amount === undefined) continue
    const amount = typeof entry.amount === "number" ? entry.amount : Number(entry.amount.trim().replace(/[$,]/g, "") || "0")
    if (!Number.isFinite(amount)) return "Enter a valid income amount."
    if (amount < 0 && sourceOfIncome !== 1) return "Negative amounts are only allowed for Exclusion income."
  }

  return null
}
